import { useQuery } from '@tanstack/react-query';
import { LuppApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import { UserIdentity, UseGetUserIdentityParams } from './dto.user-identity';

/*
|--------------------------------------------------------------------------
| GET USER IDENTITY
|--------------------------------------------------------------------------
*/

/**
 * Hook to fetch current user identity information
 * Calls the /me endpoint to get user's portal UUIDs after authentication
 * Uses the existing LuppApi service which automatically includes X-Partner-Id header
 *
 * @param params Query parameters and options
 * @returns Query result with user identity data
 *
 * @example
 * ```typescript
 * const { data: userIdentity, isLoading, error } = useGetUserIdentity({
 *   enabled: isAuthenticated, // Only fetch when user is authenticated
 * });
 * ```
 */
export const useGetUserIdentity = (params: UseGetUserIdentityParams = {}) => {
  const {
    enabled = true,
    retry: _retry = false,
    staleTime = 5 * 60 * 1000,
    refetchOnWindowFocus = false,
    refetchOnMount = false,
  } = params;

  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.USER_IDENTITY],
    queryFn: async () => {
      const response = await LuppApi.get<UserIdentity>('sales/v1/partners/me');
      return response;
    },
    enabled,
    retry: (failureCount, error) => {
      // Never retry on authentication/authorization errors
      // These indicate the user is not valid, not a temporary network issue
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403) {
          return false;
        }
      }
      // For other errors, allow one retry
      return failureCount < 1;
    },
    staleTime, // 5 minutes
    gcTime: 1000 * 60 * 60, // 1 hour - keep in cache even when no observers
    refetchOnWindowFocus,
    refetchOnMount,
  });
};

/**
 * Type guard to check if error is an auth error (401/403)
 */
type AuthError = {
  status: 401 | 403;
  message: string;
};

export const isAuthError = (error: unknown): error is AuthError => {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status?: number }).status;
    return status === 401 || status === 403;
  }
  return false;
};
